import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Lock, LogOut, Eye, Pencil, Loader2, Clock, User, Globe, Code, Award, FileText, Download, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusUpdateId, setStatusUpdateId] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [sortByScore, setSortByScore] = useState(false);

  useEffect(() => {
    const storedPass = sessionStorage.getItem('uni2-admin-pass');
    if (storedPass) {
      setPassword(storedPass);
      setIsAuthenticated(true);
      fetchProposals(storedPass, false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('uni2-admin-pass', password);
        await fetchProposals(password, sortByScore);
        toast.success('Login successful');
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    sessionStorage.removeItem('uni2-admin-pass');
    setProposals([]);
  };

  const fetchProposals = async (pass, byScore) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/proposals`, {
        params: { password: pass, sort_by_score: byScore }
      });
      setProposals(res.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (proposalId) => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }
    
    try {
      await axios.patch(`${API}/proposals/${proposalId}/status`, 
        { status: newStatus },
        { params: { password } }
      );
      toast.success('Status updated successfully');
      setStatusUpdateId(null);
      setNewStatus('');
      fetchProposals(password, sortByScore);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDownloadNDA = async (proposalId) => {
    try {
      const response = await axios.get(`${API}/proposals/${proposalId}/nda`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NDA_${proposalId}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('NDA downloaded successfully');
      fetchProposals(password, sortByScore);
    } catch (error) {
      toast.error('Failed to generate NDA');
    }
  };

  const seedDemoData = async () => {
    try {
      await axios.post(`${API}/seed-demo-data`);
      toast.success('Demo data seeded successfully');
      fetchProposals(password, sortByScore);
    } catch (error) {
      toast.error('Failed to seed demo data');
    }
  };

  const toggleSortByScore = () => {
    const newSort = !sortByScore;
    setSortByScore(newSort);
    fetchProposals(password, newSort);
  };

  const getStatusBadge = (status) => {
    const styles = {
      review: 'status-review',
      approved: 'status-approved',
      development: 'status-development',
      live: 'status-live',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || 'bg-slate-700/50 text-slate-300';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScorePriority = (score) => {
    if (score >= 80) return { label: t('score.high'), color: 'bg-green-500/20 text-green-400 border-green-500/30' };
    if (score >= 60) return { label: t('score.medium'), color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
    return { label: t('score.low'), color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#02040A]" data-testid="admin-login-page">
        <Navbar />
        
        <div className="pt-24 pb-20 flex items-center justify-center min-h-[80vh]">
          <div className="w-full max-w-md px-4">
            <div className="glass-card rounded-xl p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-white">{t('admin.login')}</h1>
                <p className="text-slate-400 text-sm mt-2">Enter admin password to continue</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('admin.password')}</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="admin-password-input"
                    required
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full glow-primary"
                  disabled={loading}
                  data-testid="admin-login-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('admin.loginBtn')
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#02040A]" data-testid="admin-dashboard">
      <Navbar />
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-slate-400 text-sm mt-1">Manage builder proposals with scoring system</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant={sortByScore ? "default" : "outline"}
                onClick={toggleSortByScore}
                data-testid="sort-by-score-btn"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Sort by Score
              </Button>
              <Button 
                variant="outline" 
                onClick={seedDemoData}
                data-testid="seed-demo-btn"
              >
                Seed Demo Data
              </Button>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('admin.logout')}
              </Button>
            </div>
          </div>

          {/* Proposals List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : proposals.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center">
              <p className="text-slate-400">No proposals yet. Click "Seed Demo Data" to add sample proposals.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {proposals.map((proposal) => {
                const priority = getScorePriority(proposal.score || 0);
                return (
                  <div 
                    key={proposal.id} 
                    className="glass-card rounded-xl p-6"
                    data-testid={`admin-proposal-${proposal.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      {/* Left - Info */}
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-white">
                            {proposal.custom_proposal_name || proposal.selected_dapp}
                          </h3>
                          <Badge className={getStatusBadge(proposal.status)}>
                            {t(`statuses.${proposal.status}`)}
                          </Badge>
                          <Badge className={priority.color}>
                            {priority.label}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-3">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {proposal.team_info.team_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Globe className="w-4 h-4" />
                            {proposal.team_info.country}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(proposal.created_at)}
                          </span>
                        </div>

                        {/* Score Display */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Award className={`w-5 h-5 ${getScoreColor(proposal.score || 0)}`} />
                            <span className={`text-2xl font-bold ${getScoreColor(proposal.score || 0)}`}>
                              {proposal.score || 0}
                            </span>
                            <span className="text-slate-500 text-sm">/100</span>
                          </div>
                          <div className="flex-grow max-w-xs">
                            <Progress value={proposal.score || 0} className="h-2" />
                          </div>
                          {proposal.nda_generated && (
                            <Badge variant="outline" className="text-green-400 border-green-500/30">
                              <FileText className="w-3 h-3 mr-1" />
                              {t('nda.generated')}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Right - Actions */}
                      <div className="flex flex-wrap items-center gap-2">
                        {statusUpdateId === proposal.id ? (
                          <div className="flex items-center gap-2">
                            <Select value={newStatus} onValueChange={setNewStatus}>
                              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white">
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0B0F19] border-white/10">
                                {['review', 'approved', 'development', 'live', 'rejected'].map((s) => (
                                  <SelectItem key={s} value={s} className="text-white hover:bg-white/10">
                                    {t(`statuses.${s}`)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button 
                              size="sm" 
                              onClick={() => handleStatusUpdate(proposal.id)}
                              data-testid={`save-status-${proposal.id}`}
                            >
                              Save
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setStatusUpdateId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setSelectedProposal(proposal);
                                setDetailsOpen(true);
                              }}
                              data-testid={`view-details-${proposal.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setStatusUpdateId(proposal.id)}
                              data-testid={`update-status-${proposal.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Status
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDownloadNDA(proposal.id)}
                              data-testid={`download-nda-${proposal.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              NDA
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="bg-[#0B0F19] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {selectedProposal?.custom_proposal_name || selectedProposal?.selected_dapp}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Submitted by {selectedProposal?.team_info.team_name}
            </DialogDescription>
          </DialogHeader>

          {selectedProposal && (
            <div className="space-y-6 mt-4">
              {/* Score Breakdown */}
              <div className="glass-card rounded-lg p-4">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-primary" /> {t('score.breakdown')}
                </h4>
                <div className="flex items-center gap-4 mb-4">
                  <span className={`text-3xl font-bold ${getScoreColor(selectedProposal.score || 0)}`}>
                    {selectedProposal.score || 0}
                  </span>
                  <span className="text-slate-400">/100 points</span>
                </div>
                {selectedProposal.score_breakdown && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">{t('score.teamInfo')}:</span>
                      <span className="text-white">{selectedProposal.score_breakdown.team_info}/30</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">{t('score.techArchitecture')}:</span>
                      <span className="text-white">{selectedProposal.score_breakdown.technical_architecture}/35</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">{t('score.timeline')}:</span>
                      <span className="text-white">{selectedProposal.score_breakdown.timeline}/15</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">{t('score.whyUni2')}:</span>
                      <span className="text-white">{selectedProposal.score_breakdown.why_uni2}/10</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">{t('score.projectSelection')}:</span>
                      <span className="text-white">{selectedProposal.score_breakdown.project_selection}/10</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Team Info */}
              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" /> Team Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400">Team Name:</span>
                    <p className="text-white">{selectedProposal.team_info.team_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Country:</span>
                    <p className="text-white">{selectedProposal.team_info.country}</p>
                  </div>
                  {selectedProposal.team_info.website && (
                    <div>
                      <span className="text-slate-400">Website:</span>
                      <p className="text-primary">{selectedProposal.team_info.website}</p>
                    </div>
                  )}
                  {selectedProposal.team_info.github && (
                    <div>
                      <span className="text-slate-400">GitHub:</span>
                      <p className="text-primary">{selectedProposal.team_info.github}</p>
                    </div>
                  )}
                </div>
                {selectedProposal.team_info.previous_projects && (
                  <div className="mt-3">
                    <span className="text-slate-400 text-sm">Previous Projects:</span>
                    <p className="text-white text-sm mt-1">{selectedProposal.team_info.previous_projects}</p>
                  </div>
                )}
              </div>

              {/* Technical Architecture */}
              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Code className="w-4 h-4" /> Technical Architecture
                </h4>
                <div className="space-y-3 text-sm">
                  {selectedProposal.technical_architecture.smart_contract && (
                    <div>
                      <span className="text-slate-400">Smart Contract Design:</span>
                      <p className="text-white">{selectedProposal.technical_architecture.smart_contract}</p>
                    </div>
                  )}
                  {selectedProposal.technical_architecture.scalability && (
                    <div>
                      <span className="text-slate-400">Scalability Strategy:</span>
                      <p className="text-white">{selectedProposal.technical_architecture.scalability}</p>
                    </div>
                  )}
                  {selectedProposal.technical_architecture.security_plan && (
                    <div>
                      <span className="text-slate-400">Security Plan:</span>
                      <p className="text-white">{selectedProposal.technical_architecture.security_plan}</p>
                    </div>
                  )}
                  {selectedProposal.technical_architecture.tech_stack && (
                    <div>
                      <span className="text-slate-400">Tech Stack:</span>
                      <p className="text-white">{selectedProposal.technical_architecture.tech_stack}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Timeline
                </h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-slate-400">MVP:</span>
                    <p className="text-white">{selectedProposal.timeline.mvp || 'TBD'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Beta:</span>
                    <p className="text-white">{selectedProposal.timeline.beta || 'TBD'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Mainnet:</span>
                    <p className="text-white">{selectedProposal.timeline.mainnet || 'TBD'}</p>
                  </div>
                </div>
              </div>

              {/* Why UNI2 */}
              {selectedProposal.why_uni2 && (
                <div>
                  <h4 className="font-semibold text-white mb-3">Why UNI2Chain?</h4>
                  <p className="text-slate-300 text-sm">{selectedProposal.why_uni2}</p>
                </div>
              )}

              {/* Custom Proposal */}
              {selectedProposal.custom_proposal_description && (
                <div>
                  <h4 className="font-semibold text-white mb-3">Custom Proposal Description</h4>
                  <p className="text-slate-300 text-sm">{selectedProposal.custom_proposal_description}</p>
                </div>
              )}

              {/* NDA Download */}
              <div className="pt-4 border-t border-white/10">
                <Button 
                  onClick={() => handleDownloadNDA(selectedProposal.id)}
                  className="w-full"
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('nda.download')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
